import React, { useContext, useEffect, useState } from 'react';
import { Alert, Spinner, Table, Badge, Modal } from 'react-bootstrap';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import moment from 'moment';

import { SurveysAPI } from '../../API';
import { getSurveyDisplayName } from '../../Utils/surveyDisplayName';
import { PageStatus } from 'enums';
import Card from '../Card/Card.js';
import CardHeader from '../Card/CardHeader';
import GridContainer from '../Grid/GridContainer';
import GridItem from '../Grid/GridItem';
import LanguageContext from '../../Layout/LanguageContext';
import { pageDict } from '../../Languages/PageTranslations';
import { RedirectHealthBanner } from '../RedirectHealth/RedirectHealth';

type StatusKey = 'Open' | 'Hold' | 'Test' | 'Wait' | 'Close';

type ProjectRow = {
    id: string;
    name: string;
    projectname: string;
    projectId: string;
    uniqueid: string;
    country: string;
    client: string;
    surveyType: StatusKey;
    publishDate: string | number | null;
    createdAt: string;
    completes: number;
};

type DashboardPayload = {
    todayStats: {
        totalProjects: number;
        liveProjects: number;
        onHoldProjects: number;
        testProjects: number;
        idsAwaitedProjects: number;
        endedProjects: number;
        completesToday: number;
        disqualifiedToday: number;
        quotafullToday: number;
        securityTermToday: number;
        terminatedToday: number;
    };
    projectsByStatus: Record<StatusKey, ProjectRow[]>;
};

type StatusSlug = 'completed' | 'disqualified' | 'quotafull' | 'securityterm';

type DetailRow = {
    // 'test' = admin's local Test rows, emitted by getTodayStatusDetails
    // alongside 'panel' and 'partner'.
    source: 'panel' | 'partner' | 'test';
    uuid: string | null;
    rid: string;
    // The three name columns are passed through raw so the row can go through
    // getSurveyDisplayName, rather than the backend collapsing them itself.
    projectname: string | null;
    childProjectName: string | null;
    parentid: string | null;
    vendorName: string;
    clientName: string;
    country: string;
    startTime: string | number | null;
    endTime: string | number | null;
};

// Card-slug → display label. Used for the modal header and nothing else;
// the slug itself is what hits the backend.
const STATUS_CARD_LABEL: Record<StatusSlug, string> = {
    completed: 'Completed',
    disqualified: 'DisQualified',
    quotafull: 'Quotafull',
    securityterm: 'Security Term',
};

// Visual grouping for the 5 status accordions. Order + labels match the
// legacy Schedule Survey status dropdown (Test / Hold / Live / IDs awaited / End).
const STATUS_ORDER: { key: StatusKey; label: string; color: string }[] = [
    { key: 'Open', label: 'Live', color: 'success' },
    { key: 'Hold', label: 'On Hold', color: 'warning' },
    { key: 'Test', label: 'Test', color: 'info' },
    { key: 'Wait', label: 'IDs Awaited', color: 'secondary' },
    { key: 'Close', label: 'Ended', color: 'dark' },
];

type KpiProps = { title: string; value: number | string; sub?: string; onClick?: () => void };
const KpiCard: React.FC<KpiProps> = ({ title, value, sub, onClick }) => {
    const clickable = typeof onClick === 'function';
    return (
        <Card>
            <CardHeader
                color="primary"
                stats={true}
                onClick={clickable ? onClick : undefined}
                style={clickable ? { cursor: 'pointer' } : undefined}
                title={clickable ? 'Click for details' : undefined}
            >
                <div style={{ padding: 8 }}>
                    <div style={{ fontSize: 12, opacity: 0.85 }}>{title}</div>
                    <div style={{ fontSize: 28, fontWeight: 600 }}>{value}</div>
                    {sub ? <div style={{ fontSize: 11, opacity: 0.7 }}>{sub}</div> : null}
                </div>
            </CardHeader>
        </Card>
    );
};

type Props = {
    role?: string;
    userId?: string;
    firstName?: string;
    selectedCountry?: string;
} & RouteComponentProps;

const PMDashboardComponent: React.FC<Props> = (props) => {
    const lang = useContext(LanguageContext) || 'en';
    const t = (key: string) =>
        (pageDict[lang] && pageDict[lang][key]) || pageDict['en'][key] || key;

    const [status, setStatus] = useState<PageStatus>(PageStatus.None);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<DashboardPayload | null>(null);

    // Modal state for the clickable today-status cards.
    // Kept separate from the main dashboard fetch so loading one doesn't reset the other.
    const [detailsModal, setDetailsModal] = useState<{
        show: boolean;
        slug: StatusSlug | '';
        rows: DetailRow[];
        loading: boolean;
    }>({ show: false, slug: '', rows: [], loading: false });

    const handleCardClick = async (slug: StatusSlug) => {
        setDetailsModal({ show: true, slug, rows: [], loading: true });
        try {
            const resp = await SurveysAPI.getTodayStatusDetails(slug);
            setDetailsModal({ show: true, slug, rows: resp?.rows || [], loading: false });
        } catch (err) {
            setDetailsModal({ show: true, slug, rows: [], loading: false });
        }
    };

    // Status cards drill down into the Surveys listing pre-filtered by surveyType.
    // Surveys.tsx reads ?surveyType= from URL on mount and on location.search change
    // (whitelist: Open/Hold/Test/Wait/Close). selectedCountry carries via Redux.
    const goToSurveys = (surveyType?: string) => {
        console.log('[CARD-CLICK]', {
            role: props.role,
            historyPresent: !!props.history,
            currentPath: props.location && props.location.pathname,
            surveyTypeArg: surveyType,
        });
        const base = `/${props.role}/surveys`;
        const url = surveyType ? `${base}?surveyType=${surveyType}` : base;
        props.history.push(url);
        console.log('[CARD-CLICK] pushed to', url);
    };

    const closeDetailsModal = () => {
        setDetailsModal({ show: false, slug: '', rows: [], loading: false });
    };

    const fmtTime = (v: string | number | null | undefined): string => {
        if (!v) return '—';
        const d = new Date(v);
        return isNaN(d.getTime()) ? '—' : moment(d).format('DD MMM YYYY, HH:mm');
    };

    useEffect(() => {
        let mounted = true;
        setStatus(PageStatus.Loading);
        SurveysAPI.pmDashboard()
            .then((resp: DashboardPayload) => {
                if (!mounted) return;
                setData(resp);
                setStatus(PageStatus.Loaded);
            })
            .catch((err) => {
                if (!mounted) return;
                setError(err?.message || 'Failed to load dashboard');
                setStatus(PageStatus.Error);
            });
        return () => {
            mounted = false;
        };
    }, [props.selectedCountry]);

    const handleOpenProject = (projectId: string) => {
        // Reuse the existing Surveys listing (same pattern other pages use for
        // drill-down). Keeps navigation consistent — we don't add a new route.
        const rolePath = props.role || 'admin';
        props.history.push(`/${rolePath}/surveys?projectId=${encodeURIComponent(projectId)}`);
    };

    const today = data?.todayStats;

    return (
        <>
            <GridContainer>
                <div className="w-100 mb-3">
                    <CardHeader color="primary">
                        <div className="d-flex align-items-center justify-content-between">
                            <h4 style={{ margin: 0 }}>
                                {t('Dashboard')}
                                {props.firstName ? ` — ${props.firstName}` : ''}
                            </h4>
                            <small style={{ opacity: 0.8 }}>
                                {t('Showing projects owned by you')}
                            </small>
                        </div>
                    </CardHeader>
                </div>
            </GridContainer>

            <RedirectHealthBanner
                href={`/${props.role || 'admin'}/redirect-health`}
                onOpen={(href) => props.history.push(href)}
            />

            <Alert variant="danger" show={status === PageStatus.Error}>
                {error}
            </Alert>

            {status === PageStatus.Loading && (
                <div className="d-flex justify-content-center p-4">
                    <Spinner animation="border" />
                </div>
            )}

            {/* Today-status details modal (opens from the 4 clickable cards). */}
            <Modal
                show={detailsModal.show}
                onHide={closeDetailsModal}
                size="lg"
                backdrop="static"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {detailsModal.slug ? STATUS_CARD_LABEL[detailsModal.slug] : ''} — {t("Today's Details")} ({detailsModal.rows.length})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailsModal.loading ? (
                        <div className="d-flex justify-content-center p-4"><Spinner animation="border" /></div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <Table size="sm" bordered striped hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{t('Survey Unique Id')}</th>
                                        <th>{t('RID')}</th>
                                        <th>{t('Project Name')}</th>
                                        <th>{t('Vendor Name')}</th>
                                        <th>{t('Client Name')}</th>
                                        <th>{t('Country')}</th>
                                        <th>{t('Start Time')}</th>
                                        <th>{t('End Time')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailsModal.rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={9} className="text-center">{t('No records found for today.')}</td>
                                        </tr>
                                    ) : (
                                        detailsModal.rows.map((r, i) => (
                                            <tr key={`${r.source}-${r.rid}-${i}`}>
                                                <td>{i + 1}</td>
                                                <td>{r.uuid || '—'}</td>
                                                <td>{r.rid || '—'}</td>
                                                <td>{getSurveyDisplayName(r) || '—'}</td>
                                                <td>{r.vendorName || '—'}</td>
                                                <td>{r.clientName || '—'}</td>
                                                <td>{r.country || '—'}</td>
                                                <td>{fmtTime(r.startTime)}</td>
                                                <td>{fmtTime(r.endTime)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {status === PageStatus.Loaded && !!today && (<>
                <GridContainer>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('Total Projects')} value={today!.totalProjects} onClick={() => goToSurveys()} />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('Live')} value={today!.liveProjects} onClick={() => goToSurveys('Open')} />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('On Hold')} value={today!.onHoldProjects} onClick={() => goToSurveys('Hold')} />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('Test')} value={today!.testProjects} onClick={() => goToSurveys('Test')} />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('IDs Awaited')} value={today!.idsAwaitedProjects} onClick={() => goToSurveys('Wait')} />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={2}>
                        <KpiCard title={t('Ended')} value={today!.endedProjects} onClick={() => goToSurveys('Close')} />
                    </GridItem>
                </GridContainer>

                {/* Today's respondent breakdown — each card is clickable and opens
                    a modal with UUID/RID/Project/Vendor/Client/Country/Start/End rows. */}
                <GridContainer>
                    <GridItem xs={12} sm={6} md={3} lg={3}>
                        <KpiCard
                            title={t("Today's Completed")}
                            value={today!.completesToday}
                            sub={t('Click for details')}
                            onClick={() => handleCardClick('completed')}
                        />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={3}>
                        <KpiCard
                            title={t("Today's DisQualified")}
                            value={today!.disqualifiedToday || 0}
                            sub={t('Click for details')}
                            onClick={() => handleCardClick('disqualified')}
                        />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={3}>
                        <KpiCard
                            title={t("Today's Quotafull")}
                            value={today!.quotafullToday || 0}
                            sub={t('Click for details')}
                            onClick={() => handleCardClick('quotafull')}
                        />
                    </GridItem>
                    <GridItem xs={12} sm={6} md={3} lg={3}>
                        <KpiCard
                            title={t("Today's Security Term")}
                            value={today!.securityTermToday || 0}
                            sub={t('Click for details')}
                            onClick={() => handleCardClick('securityterm')}
                        />
                    </GridItem>
                </GridContainer>

                <div className="jumbotron bg-white p-3 border shadow-sm mt-3">
                    <div className="mb-3">{t('Projects by Status')}</div>

                    {STATUS_ORDER.map(({ key, label }) => {
                        const rows = (data && data.projectsByStatus[key]) || [];
                        return (
                            <Accordion key={key} defaultExpanded={key === 'Open'}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            paddingRight: 16,
                                        }}
                                    >
                                        <span style={{ fontWeight: 600 }}>{t(label)}</span>
                                        <Badge pill variant="primary">
                                            {rows.length}
                                        </Badge>
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails style={{ display: 'block', padding: 0 }}>
                                    {rows.length === 0 ? (
                                        <div style={{ padding: 16, opacity: 0.7 }}>
                                            {t('No projects in this status')}
                                        </div>
                                    ) : (
                                        <Table striped bordered hover responsive className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th>{t('Project Name')}</th>
                                                    <th>{t('Project ID')}</th>
                                                    <th>{t('Unique ID')}</th>
                                                    <th>{t('Client')}</th>
                                                    <th>{t('Country')}</th>
                                                    <th>{t('Completes')}</th>
                                                    <th>{t('Start Date')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((r) => (
                                                    <tr
                                                        key={r.id}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleOpenProject(r.id)}
                                                    >
                                                        <td>{r.projectname || r.name || '—'}</td>
                                                        <td>{r.projectId || '—'}</td>
                                                        <td>{r.uniqueid || '—'}</td>
                                                        <td>{r.client || '—'}</td>
                                                        <td>{r.country || '—'}</td>
                                                        <td>{r.completes || 0}</td>
                                                        <td>
                                                            {r.publishDate
                                                                ? moment(r.publishDate).format('DD MMM YYYY')
                                                                : r.createdAt
                                                                ? moment(r.createdAt).format('DD MMM YYYY')
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </div>
            </>)}
        </>
    );
};

function mapStateToProps(state: any) {
    const u = state.adminUser.adminUser || {};
    return {
        role: u.role,
        userId: u.userId,
        firstName: u.firstName,
        selectedCountry: u.selectedCountry,
    };
}

export const PMDashboard = withRouter(connect(mapStateToProps)(PMDashboardComponent));
export default PMDashboard;
